<template>
  <div class="flex1">
    <div class="row mb-3">
      <div class="col-12">
        <div class="parameter-header">
          <h1>{{ parameter.key }}</h1>
          <HistoryModal
            v-if="parameter.id"
            :parameter-id="parameter.id"
            :parameter-name="parameter.key"
            :current-parameter="parameter"
            :show-button-text="true"
            button-class="history-button"
          />
        </div>
        <div class="row desc-row">
          <div class="col-md-3 col-12 center-para bordered">
            <textarea
              v-if="isEditing(parameter.id)"
              :id="`edit-param-${parameter.id}`"
              :value="parameter.desc"              
              type="text"
              class="form-control opening-input"
              placeholder="Description"
            />
            <span v-else>
              {{ parameter.desc }}
            </span>
          </div>
          <div class="col-md-2 col-12 center-para">
            <div v-if="isEditing(parameter.id)">
              <button
                class="btn btn-sm btn-success mx-1"
                @click="editDescription(parameter.id)"
              >
                Save
              </button>
              <button
                class="btn btn-sm btn-secondary mx-1"
                @click="cancelEdit(parameter.id)"
              >
                Cancel
              </button>
            </div>
            <button
              v-else
              class="btn btn-sm btn-info mx-1"
              @click="activateEdit(parameter.id)"
            >
              Edit Description
            </button>
          </div>
          <div class="col-md-3 col-12 center-para bordered">
            <div v-if="isEditing(parameter.id + '-tag')">
              <input
                :id="`edit-tag-${parameter.id}`"
                :value="parameter.tag"
                type="text"
                class="textarea"
                placeholder="Tag"
                list="tags"
              />
              <datalist id="tags">
                <option v-for="tag in tags" :key="tag" :value="tag">{{
                  tag
                }}</option>
              </datalist>
            </div>
            <span v-else>
              {{ parameter.tag }}
            </span>
          </div>
          <div class="col-md-1 col-12 center-para">
            <div v-if="isEditing(parameter.id + '-tag')">
              <button
                class="btn btn-sm btn-success mx-1"
                @click="editTag(parameter.id)"
              >
                Save
              </button>
              <button
                class="btn btn-sm btn-secondary mx-1"
                @click="cancelEdit(parameter.id + '-tag')"
              >
                Cancel
              </button>
            </div>
            <button
              v-else
              class="btn btn-sm btn-info mx-1"
              @click="activateEdit(parameter.id + '-tag')"
            >
              Edit Tag
            </button>
          </div>
          <div class="col-md-2 col-12 center-para mr-200">
            <label>Filter Categories</label>
            <select  class="form-control"  id="select-category" v-model="filterOptions.category">
              
            <option disabled value="">Please select a value</option>
            <option
              v-for="category in categories"
              :key="`category-${category}`"
              :value="category"
            >
              {{ category }}
            </option>
          </select>
          </div>
        </div>
      </div>
    </div>
    <div class="row titles">
      <div class="col-md-3 col-12">
        Audience
      </div>
      <div class="col-md-5 col-12">
        Value
      </div>
      <div class="col-md-2 col-12">
        Priority
        <Tooltip :content="priorityInfo"/>
      </div>
      <div class="col-md-2 col-12"></div>
    </div>
    <div class="row">
      <div class="col-md-3 col-12">
        <select
          v-model="newValue.audience"
          class="form-control"
          id="select-audience"
        >
          <option disabled value="">Please select an operator</option>
          <option value="default">Default</option>
          <option
            v-for="audience in audiences"
            :key="'opSelect-' + audience.id"
            :value="audience.id"
          >
            {{ audience.name }}
          </option>
        </select>
        <div class="send-toggle-row" style="margin-top: 1rem;">
          <span class="send-toggle-text">Send parameter</span>
          <label class="toggle-switch">
            <input type="checkbox" :checked="!newValueDoNotSend" @change="newValueDoNotSend = !$event.target.checked">
            <span class="toggle-slider"></span>
          </label>
          <Tooltip :content="'When disabled, this parameter will not be included in the Config API response if the user receives this audience value based on the priority logic.'"/>
        </div>
      </div>
      <div class="col-md-5 col-12 pre">
        <div class="d-flex">
          <textarea
            class="form-control new-value-input"
            :value="newValue.value"
            @input="debouncedInput"
            placeholder="Enter value or use JSON editor for complex JSON"
            id="new-value"
            rows="3"
            @click="getFocusedItem()"
            @paste="handlePaste"
            style="resize: vertical; font-family: Poppins; font-size: 12px;"
            :disabled="newValueDoNotSend"
          ></textarea>
          <JsonModal 
            v-if="showJsonModal"
            :value-object="jsonModalValue" 
            @json-submitted="handleJsonSubmitted" 
            @json-progress-saved="handleJsonProgressSaved"
            @json-submitted-to-db="handleJsonSubmittedToDb"
            :customStyle="{marginLeft: '10px', marginTop: '0px'}" 
            :title="'JSON'"
            :editMode="true"
            :mode="'add'"
            :directOpen="true"
            @modal-closed="closeJsonModal"
            :parameterName="parameter.key"
            :audienceName="newValue.audience === 'default' ? 'Default' : (newValue.audience && audience(newValue.audience).name ? audience(newValue.audience).name : '')"
            :audience="newValue.audience !== 'default' && newValue.audience ? audience(newValue.audience) : null"
            :currentCategory="newValue.category || ''"
            :currentPriority="newValue.priority || ''"
            :categories="categories"
          />
          <button 
            v-else
            type="button" 
            class="btn btn-primary"
            :style="{maxHeight: '30px',lineHeight: '0px', marginLeft: '10px', marginTop: '0px'}" 
            @click="openJsonModal"
            :disabled="newValueDoNotSend"
          >
            JSON
          </button>
        </div>
      </div>
      <div class="col-md-2 col-12">
        <input
          :disabled="newValue.audience == 'default'"
          class="form-control"
          type="number"
          v-model.number="newValue.priority"
          placeholder="Priority"
        />
      </div>
      <div class="col-md-2 col-12 text-center">
        <button class="btn btn-sm btn-success mx-2" @click="addNewValue()">
          ADD
        </button>
      </div>
    </div>
    <hr class="dashed" />

    <div class="row params-hover line-hover ">
      <div class="col-md-3 col-12 center-para">
        Default
        <span v-if="pendingChanges['default']" class="badge badge-warning ml-2" style="font-size: 11px;" title="Local changes not yet submitted to database">
          <i class="fas fa-exclamation-circle"></i> Local Changes - Pending Submission
        </span>
        <div class="send-toggle-row">
          <span class="send-toggle-text">Send parameter</span>
          <label class="toggle-switch" :class="{ disabled: updatingDoNotSend['default'] }">
            <input
              type="checkbox"
              :checked="parameter.default_value !== null"
              @change="toggleDoNotSend('default', $event)"
              :disabled="updatingDoNotSend['default']"
            >
            <span class="toggle-slider"></span>
          </label>
          <span v-if="updatingDoNotSend['default']" class="toggle-spinner">
            <i class="fas fa-spinner fa-spin"></i>
          </span>
        </div>
      </div>
      <div class="col-md-4 col-12 pre aud-val">
        <div class="bordered">
          <div v-if="isEditing('default')">
            <textarea
              type="text"
              class="textarea"
              @click="
                getFocusedItem();
                textAreaAdjust(`edit-value-default`);
              "
              :value="formatForEdit(parameter.default_value)"
              :id="`edit-value-default`"
            >
            </textarea>
          </div>
          <div v-else>
            <template v-if="parameter.default_value === null">
              <span class="not-sent-badge">
                <i class="fas fa-ban"></i> Not sent to client
              </span>
            </template>
            <template v-else>
              <button
                class="btn btn-sm action-button fa fa-clipboard"
                aria-hidden="true"
                @click="copyParameterValue(parameter.default_value)"
              ></button>
              <div class="value pre" @click="openReadOnlyModal(parameter.default_value, 'Default', 'default')" style="cursor: pointer;" title="Click to view in read-only mode">
                {{ parameter.default_value | jsonFormat }}
              </div>
            </template>
          </div>
        </div>
        <div v-if="isEditing('default')">
            <button 
              type="button" 
              class="btn btn-primary btn-sm"
              @click="openDefaultJsonModal"
            >
              Edit JSON
            </button>
            <JsonModal 
              v-if="showDefaultJsonModal"
              :value-object="defaultJsonModalValue" 
              @json-submitted="handleDefaultJsonSubmitted" 
              @json-progress-saved="handleDefaultJsonProgressSaved"
              @json-submitted-to-db="handleDefaultJsonSubmittedToDb"
              :title="'Edit Default JSON'"
              :editMode="true"
              :mode="'edit'"
              :directOpen="true"
              @modal-closed="closeDefaultJsonModal"
              :parameterName="parameter.key"
              audienceName="Default"
              :showCategoryPriority="false"
            />
        </div>
      </div>
      <div  class="col-md-1 col-12  center-para">
        <label   for="percentage">Category:</label>
        Default
      </div>
      <div class="col-md-2 col-12 center-para">
        -
      </div>
      <div class="col-md-2 col-12 center-para text-center mt-1">
        <div v-if="isEditing('default')">
          <button
            class="btn btn-sm btn-success mx-2"
            @click="editAudience('default')"
          >
            Save
          </button>
          <button
            class="btn btn-sm btn-secondary mx-1"
            @click="cancelEdit('default')"
          >
            Cancel
          </button>
        </div>
        <div v-else>
          <button
            class="btn btn-sm btn-info mx-2"
            @click="activateEdit('default')"
          >
            Edit
          </button>
          <button
            class="btn btn-sm btn-danger mx-2"
            @click="deleteConditionalValue('default')"
          >
            Del
          </button>
        </div>
      </div>
    </div>

    <div
      class="row params-hover line-hover"
      v-for="(key, index) in sortedKeys"
      :key="`parameter-${index}-${key}`"
    >
      <div class="col-md-3 col-12 center-para ">
        {{ audience(key).name }}
        <span v-if="pendingChanges[key]" class="badge badge-warning ml-2" style="font-size: 11px;" title="Local changes not yet submitted to database">
          <i class="fas fa-exclamation-circle"></i> Local Changes - Pending Submission
        </span>
        <div class="send-toggle-row">
          <span class="send-toggle-text">Send parameter</span>
          <label class="toggle-switch" :class="{ disabled: updatingDoNotSend[key] }">
            <input
              type="checkbox"
              :checked="parameter.audience_values[key] !== null"
              @change="toggleDoNotSend(key, $event)"
              :disabled="updatingDoNotSend[key]"
            >
            <span class="toggle-slider"></span>
          </label>
          <span v-if="updatingDoNotSend[key]" class="toggle-spinner">
            <i class="fas fa-spinner fa-spin"></i>
          </span>
        </div>
      </div>
      <div class="col-md-4 col-12 pre  aud-val">
        <div class="bordered">
          <div v-if="isEditing(audience(key).id)">
            <textarea
              type="text"
              class="textarea"
              @click="
                getFocusedItem();
                textAreaAdjust(`edit-value-${audience(key).id}`);
              "
              :value="formatForEdit(parameter.audience_values[key])"
              :id="`edit-value-${audience(key).id}`"
            >
            </textarea>
          </div>
          <div v-else>
            <template v-if="parameter.audience_values[key] === null">
              <span class="not-sent-badge">
                <i class="fas fa-ban"></i> Not sent to client
              </span>
            </template>
            <template v-else>
              <button
                class="btn btn-sm action-button fa fa-clipboard"
                aria-hidden="true"
                @click="copyParameterValue(parameter.audience_values[key])"
              ></button>
              <div class="value text-hover" @click="openReadOnlyModal(parameter.audience_values[key], audience(key).name, audience(key).id)" style="cursor: pointer;" title="Click to view in read-only mode">
                {{ parameter.audience_values[key] | jsonFormat }}
              </div>
            </template>
          </div>

        </div>
        <div v-if="isValidJson(parameter.audience_values[key]) && isEditing(audience(key).id)">
            <button 
              type="button" 
              class="btn btn-primary btn-sm"
              @click="openAudienceJsonModal(key)"
            >
              Edit JSON
            </button>
            <JsonModal 
              v-if="showAudienceJsonModal && currentAudienceKey === key"
              :value-object="audienceJsonModalValue" 
              @json-submitted="handleAudienceJsonSubmitted" 
              @json-progress-saved="handleAudienceJsonProgressSaved"
              @json-submitted-to-db="handleAudienceJsonSubmittedToDb"
              :title="'Edit Audience JSON'"
              :editMode="true"
              :mode="'edit'"
              :directOpen="true"
              @modal-closed="closeAudienceJsonModal"
              :parameterName="parameter.key"
              :audienceName="audience(currentAudienceKey).name || ''"
              :audience="audience(currentAudienceKey)"
              :currentCategory="parameter.categories ? parameter.categories[currentAudienceKey] : ''"
              :currentPriority="parameter.priorities ? parameter.priorities[currentAudienceKey] : ''"
              :categories="categories"
            />
        </div>
      </div>
      <div class="col-md-1 col-12 center-para">
      <select v-model="parameter.categories[key]" class="form-control"  id="select-category">
        <option disabled value="">Please select a value</option>
        <option
          v-for="category in categories"
          :key="`category-${category}`"
          :value="category"
        >
          {{ category }}
        </option>
      </select>
      </div>
      <div class="col-md-2 col-12 center-para">
        <input
          class="form-control"
          type="number"
          :value="priorities ? priorities[key] : undefined"
          :id="key + '-priority'"
          placeholder="priority"
          @change="changePriority(key)"
        />
      </div>
      <div class="col-md-2 col-12 center-para text-center mt-1">
        <div v-if="isEditing(audience(key).id)">
          <button
            class="btn btn-sm btn-success mx-2"
            @click="editAudience(audience(key).id)"
          >
            Save
          </button>
          <button
            class="btn btn-sm btn-secondary mx-1"
            @click="cancelEdit(audience(key).id)"
          >
            Cancel
          </button>
        </div>
        <div v-else>
          <button
            class="btn btn-sm btn-info mx-2"
            @click="activateEdit(audience(key).id)"
          >
            Edit
          </button>
          <button
            class="btn btn-sm btn-danger mx-2"
            @click="deleteConditionalValue(audience(key).id)"
          >
            Del
          </button>
        </div>
      </div>
    </div>
    
    <!-- Read-only JSON Modal -->
    <JsonModal 
      v-if="showReadOnlyModal"
      :value-object="readOnlyModalValue" 
      :title="'View JSON (Read-Only)'"
      :editMode="false"
      :mode="'edit'"
      :directOpen="true"
      @modal-closed="closeReadOnlyModal"
      @mode-changed="handleReadOnlyModeChanged"
      @json-submitted="handleReadOnlyJsonSubmitted"
      @json-progress-saved="handleReadOnlyJsonProgressSaved"
      @json-submitted-to-db="handleReadOnlyJsonSubmittedToDb"
      :parameterName="parameter.key"
      :audienceName="readOnlyAudienceName"
      :audience="readOnlyAudienceId ? audience(readOnlyAudienceId) : null"
      :currentCategory="readOnlyAudienceId && readOnlyAudienceId !== 'default' && parameter.categories ? parameter.categories[readOnlyAudienceId] : ''"
      :currentPriority="readOnlyAudienceId && readOnlyAudienceId !== 'default' && parameter.priorities ? parameter.priorities[readOnlyAudienceId] : ''"
      :categories="categories"
      :showCategoryPriority="readOnlyAudienceId !== 'default'"
    />
    
    <div class="row mt-2">
      <div class="col-12">
        <button class="btn btn-info" @click="validateJSONInput(false)">
          Validate JSON input
        </button>
      </div>
    </div>
    <!-- Modal -->
    <div
      class="modal fade"
      id="warning-modal"
      tabindex="-1"
      role="dialog"
      aria-labelledby="exampleModalCenterTitle"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="exampleModalLongTitle">
              <span style="color: red;">NOT a valid JSON</span>
            </h5>
            <button
              type="button"
              class="close"
              data-dismiss="modal"
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <span id="warning-modal-text" style="color: black;"
              >Parameter is NOT a valid JSON but it is edited and saved.</span
            >
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              data-dismiss="modal"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
  
</template>

<script>
import notify from "../util/notification";
import JsonModal from "../components/common/JsonModal.vue";
import HistoryModal from "../components/common/HistoryModal.vue";
import Validator from "../util/validator";
import Tooltip from "../components/common/Tooltip.vue";
import info from "../constants/info";

export default {
  components: {
    JsonModal,
    HistoryModal,
    Tooltip
  },
  data() {
    return {
      priorityInfo: info.parameters.priority,
      newValue: {

      },
      filterOptions: {
        category:""
      },
      default_value : "",
      editingValues: [],
      focusedItem: "",
      tags: [],
      categories: ["Internal", "External", "Other"],
      category: "Other",
      sortedKeys: [],
      showJsonModal: false,
      jsonModalValue: "",
      showDefaultJsonModal: false,
      defaultJsonModalValue: "",
      showAudienceJsonModal: false,
      audienceJsonModalValue: "",
      currentAudienceKey: null,
      showReadOnlyModal: false,
      readOnlyModalValue: "",
      readOnlyAudienceName: "",
      readOnlyAudienceId: null,
      pendingChanges: {}, // Track which audiences have pending local changes
      originalValues: {}, // Store original DB values to detect changes
      newValueDoNotSend: false,
      updatingDoNotSend: {},
    };
  },
  filters: {
    jsonFormat: function(value) {
      if (value === null) return "(not sent)";
      if (!value) return "";
      try {
        let object = JSON.parse(value);
        if (object && typeof object === "object") {
          // Use the built-in JSON.stringify for safe and standard formatting.
          // This correctly handles escape characters like \n within strings.
          return JSON.stringify(object, null, "\t");
        }
      } catch (e) {
        return value;
      }
      return value;
    },
  },
  computed: {
    
    // Re-arranges the parameter object based on priority of the audiences. Displays audiences without priorities in the end
    parameter() {
      try {
        const parameters = this.$store.getters.parameters;
        if (parameters.length > 0) {
          parameters.forEach((parameter) => {
            if (parameter.tag) {
              if (!this.tags.includes(parameter.tag))
                this.tags.push(parameter.tag);
            }
          });
          const parameter = parameters.find(
            (el) => el.id == this.$route.params.id
          );
          if (parameter) {
            if(!parameter.categories) parameter.categories = {};
            
            if (this.default_value && this.default_value !== "")
            {
              parameter.default_value = this.default_value
            } 
            // Create a new object with sorted keys
            const sortedObj = { ...parameter };
            if(sortedObj.audience_values)
               this.sortedKeys = this.sortKeys(sortedObj.audience_values, sortedObj.priorities)
            for (const key of this.sortedKeys) {
              sortedObj.audience_values[key] = parameter.audience_values[key];
            }
       
            return sortedObj;
          }
        }
        return {};
      } catch (error) {
        console.log("catch block in parameter computed: ", error);
      }
    },
    audiences() {
      const audiences = this.$store.getters.audiences;
      if (audiences)
        return audiences.sort((a, b) => a.name.localeCompare(b.name));
      else return [];
    },
    priorities() {
      return this.parameter.priorities ? this.parameter.priorities : {};
    },
  },
  watch: {
    // Watch for parameter changes from store to update original values
    'parameter.default_value': {
      handler(newVal) {
        if (newVal !== undefined && !this.pendingChanges['default']) {
          this.originalValues['default'] = this.normalizeValue(newVal);
        }
      },
      immediate: true
    },
    'parameter.audience_values': {
      handler(newVal) {
        if (newVal) {
          Object.keys(newVal).forEach(key => {
            if (!this.pendingChanges[key]) {
              this.originalValues[key] = this.normalizeValue(newVal[key]);
            }
          });
        }
      },
      deep: true,
      immediate: true
    }
  },
  beforeDestroy() {
    // Clean up timeouts to prevent memory leaks
    if (this._inputTimeout) {
      clearTimeout(this._inputTimeout);
    }
  },
  methods: {
    // Normalize value to a consistent format for comparison
    normalizeValue(value) {
      if (value === null) return null;
      if (!value && value !== 0 && value !== false) return '';
      // For JSON objects/arrays, normalize to compact form without whitespace
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          // If it's valid JSON, re-stringify it in a normalized form
          return JSON.stringify(parsed);
        } catch (e) {
          // Not JSON, return as-is (trimmed)
          return value.trim();
        }
      }
      return JSON.stringify(value);
    },
    isObject : Validator.isObject,
    isValidJson : Validator.isValidJson,
    formatForEdit(value) {
      if (value === null) return '';
      return this.$options.filters.jsonFormat(value);
    },
    
    // Generate audience names map for history tracking
    getAudienceNamesMap() {
      const map = {};
      if (this.audiences) {
        this.audiences.forEach(aud => {
          map[aud.id] = aud.name;
        });
      }
      return map;
    },
    // sorts the parameters based on their priorities. IF a priority does not exists, moves it to the end
    sortKeys(audience_values,priorities) {
      try {
        // Get keys from the object and optionally sort them
        if(!audience_values) return [];
        const audienceKeys = Object.keys(audience_values);
        const sortedKeys =  audienceKeys.sort((a, b) => {
    
        if(priorities && priorities[a] !== undefined && priorities[b] !== undefined) {
          // Both have priorities - sort by priority in descending order (higher priority first)
          return priorities[b] - priorities[a];
        } else if (priorities && priorities[a] === undefined && priorities[b] !== undefined) {
          // a has no priority, b has priority - a should come after b
          return 1;
        } else if (priorities && priorities[a] !== undefined && priorities[b] === undefined) {
          // a has priority, b has no priority - a should come before b
          return -1;
        } else {
          // Neither has priority - maintain original order
          return 0;
        }
        });
        return sortedKeys;
        
      } catch (error) {
        console.log("catch block in sortedKeys computed: ", error);
        return []
      }
    },
    audience(key) {
      const audience = this.audiences.find((item) => item.id == key);
      return audience ? audience : {};
    },
    editTag(id) {
      const data = JSON.parse(JSON.stringify(this.parameter));
      delete data.id;
      data["tag"] = document.getElementById("edit-tag-" + id).value;
      this.$store.dispatch("configEdit", {
        type: "parameter",
        payload: { id, data, audienceNames: this.getAudienceNamesMap() },
      });
      this.editingValues = this.editingValues.filter(
        (item) => item != id + "-tag"
      );
    },
    cancelEdit(id) {
      this.editingValues = this.editingValues.filter((item) => item != id);
    },
    editDescription(id) {
      const data = JSON.parse(JSON.stringify(this.parameter));
      delete data.id;
      data.desc = document.getElementById("edit-param-" + id).value;
      this.$store.dispatch("configEdit", {
        type: "parameter",
        payload: { id, data, audienceNames: this.getAudienceNamesMap() },
      });
      this.editingValues = this.editingValues.filter((item) => item != id);
    },
    copyParameterValue(value) {
      if (value === null || value === undefined) {
        notify("No value to copy", "error");
        return;
      }
      try {
        const tempInput = document.createElement("input");
        tempInput.value = value;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand("copy");
        document.body.removeChild(tempInput);
        notify("Copied to clipboard", "success");
      } catch (error) {
        notify("Value does not exist", "error");
      }
    },
    async toggleDoNotSend(audienceKey, event) {
      const isEnabling = event.target.checked;
      const label = audienceKey === 'default' ? 'default value' : this.audience(audienceKey).name;
      const confirmMessage = isEnabling
        ? `Are you sure you want to enable sending for "${label}"? This will reset its value to empty.`
        : `Are you sure you want to disable sending for "${label}"? When disabled, this parameter will not be included in the Config API response if the user receives this audience value based on the priority logic.`;

      if (!confirm(confirmMessage)) {
        event.target.checked = !isEnabling;
        return;
      }

      const newValue = isEnabling ? '' : null;
      this.$set(this.updatingDoNotSend, audienceKey, true);
      try {
        const data = { ...this.parameter, audience_values: { ...this.parameter.audience_values } };
        const id = data.id;
        delete data.id;
        if (audienceKey === 'default') {
          data.default_value = newValue;
        } else {
          data.audience_values[audienceKey] = newValue;
        }
        await this.$store.dispatch('configEdit', {
          type: 'parameter',
          payload: { id, data, audienceNames: this.getAudienceNamesMap() }
        });
        notify(
          newValue === null
            ? `Parameter will not be sent for ${label}`
            : `Parameter value reset for ${label}`,
          'success'
        );
      } catch (error) {
        notify('Failed to update. Please try again.', 'error');
        event.target.checked = !event.target.checked;
      } finally {
        this.$set(this.updatingDoNotSend, audienceKey, false);
      }
    },
    textAreaAdjust(id) {
      const element = document.getElementById(id);
      if (element.style.height < element.scrollHeight) {
        element.style.height = "auto";
        element.style.height = element.scrollHeight + "px";
      }
    },
    isNotFiltered(key){
      // check if the key is not filtered
      if(this.filterOptions.category){
        if(this.parameter.categories[key] != this.filterOptions.category) return false;
      }
    },

    // Sets value text field's value from the json editor modal
    setNewValue(val){
      this.$set(this.newValue, 'value', val);
    },
    
    // Handle JSON submission from modal
    handleJsonSubmitted(val) {
      this.setNewValue(val);
      this.closeJsonModal();
    },
    
    // Opens the JSON modal with current value
    openJsonModal() {
      // Only set the value when actually opening the modal
      this.jsonModalValue = this.newValue.value || '{}';
      this.showJsonModal = true;
    },
    
    // Closes the JSON modal
    closeJsonModal() {
      this.showJsonModal = false;
      // Clear the modal value to prevent any processing
      this.jsonModalValue = '';
    },
    
    // Default JSON modal handlers
    openDefaultJsonModal() {
      this.defaultJsonModalValue = this.parameter.default_value || '{}';
      this.showDefaultJsonModal = true;
    },
    
    handleDefaultJsonSubmitted(val) {
      this.changeAudienceValue('default', val);
      this.closeDefaultJsonModal();
    },
    
    closeDefaultJsonModal() {
      this.showDefaultJsonModal = false;
      this.defaultJsonModalValue = '';
    },
    
    // Audience JSON modal handlers
    openAudienceJsonModal(key) {
      this.currentAudienceKey = key;
      this.audienceJsonModalValue = this.parameter.audience_values[key] || '{}';
      this.showAudienceJsonModal = true;
    },
    
    handleAudienceJsonSubmitted(val) {
      this.changeAudienceValue(this.currentAudienceKey, val);
      this.closeAudienceJsonModal();
    },
    
    closeAudienceJsonModal() {
      this.showAudienceJsonModal = false;
      this.audienceJsonModalValue = '';
      this.currentAudienceKey = null;
    },
    
    // Read-only modal handlers
    openReadOnlyModal(value, audienceName = '', audienceId = null) {
      this.readOnlyModalValue = value || '{}';
      this.readOnlyAudienceName = audienceName;
      this.readOnlyAudienceId = audienceId;
      this.showReadOnlyModal = true;
    },
    
    handleReadOnlyModeChanged(isEditMode) {
      // Mode switching is now handled within the modal
      // Users can submit directly to database from the modal
      console.log('Edit mode changed to:', isEditMode);
    },
    
    handleReadOnlyJsonSubmitted(val) {
      // Update the value in the UI
      this.changeAudienceValue(this.readOnlyAudienceId, val);
      // Close the modal
      this.closeReadOnlyModal();
      // The Save button is now visible because we activated edit mode
    },
    
    closeReadOnlyModal() {
      this.showReadOnlyModal = false;
      this.readOnlyModalValue = '';
      this.readOnlyAudienceName = '';
      this.readOnlyAudienceId = null;
    },
    
    // Progress save handlers - update local state without closing modal
    handleJsonProgressSaved(data) {
      this.setNewValue(data.value);
      // Update category and priority if provided
      if (data.category) {
        this.newValue.category = data.category;
      } else {
        delete this.newValue.category;
      }
      if (data.priority !== undefined && data.priority !== '') {
        this.newValue.priority = Number(data.priority);
      } else {
        delete this.newValue.priority;
      }
      // Track pending changes by comparing with original value
      if (this.newValue.audience) {
        const currentValue = this.normalizeValue(data.value);
        const originalValue = this.originalValues[this.newValue.audience];
        const hasPendingChanges = currentValue !== originalValue;
        this.$set(this.pendingChanges, this.newValue.audience, hasPendingChanges);
      }
    },
    
    handleDefaultJsonProgressSaved(data) {
      this.changeAudienceValue('default', data.value);
      // Default doesn't have category/priority, but keep for consistency
      // Track pending changes by comparing with original value
      const currentValue = this.normalizeValue(data.value);
      const originalValue = this.originalValues['default'];
      const hasPendingChanges = currentValue !== originalValue;
      this.$set(this.pendingChanges, 'default', hasPendingChanges);
    },
    
    handleAudienceJsonProgressSaved(data) {
      this.changeAudienceValue(this.currentAudienceKey, data.value);
      // Update category and priority in parameter
      if (data.category) {
        this.$set(this.parameter.categories, this.currentAudienceKey, data.category);
      }
      if (data.priority !== undefined && data.priority !== '') {
        if (!this.parameter.priorities) this.parameter.priorities = {};
        this.$set(this.parameter.priorities, this.currentAudienceKey, Number(data.priority));
      }
      // Track pending changes by comparing with original value
      const currentValue = this.normalizeValue(data.value);
      const originalValue = this.originalValues[this.currentAudienceKey];
      const hasPendingChanges = currentValue !== originalValue;
      this.$set(this.pendingChanges, this.currentAudienceKey, hasPendingChanges);
    },
    
    handleReadOnlyJsonProgressSaved(data) {
      this.changeAudienceValue(this.readOnlyAudienceId, data.value);
      // Update category and priority in parameter
      if (this.readOnlyAudienceId !== 'default') {
        if (data.category) {
          this.$set(this.parameter.categories, this.readOnlyAudienceId, data.category);
        }
        if (data.priority !== undefined && data.priority !== '') {
          if (!this.parameter.priorities) this.parameter.priorities = {};
          this.$set(this.parameter.priorities, this.readOnlyAudienceId, Number(data.priority));
        }
      }
      // Track pending changes by comparing with original value
      const currentValue = this.normalizeValue(data.value);
      const originalValue = this.originalValues[this.readOnlyAudienceId];
      const hasPendingChanges = currentValue !== originalValue;
      this.$set(this.pendingChanges, this.readOnlyAudienceId, hasPendingChanges);
    },
    
    // DB submit handlers - save to database
    handleJsonSubmittedToDb(data) {
      // Validate audience is selected (for add mode)
      if (!this.newValue.audience) {
        alert('Please select an audience before submitting to database');
        return;
      }
      
      // Check for duplicate audience
      if (this.newValue.audience === 'default' && this.parameter.default_value) {
        if (!confirm('Default value already exists. Do you want to replace it?')) {
          return;
        }
      } else if (this.newValue.audience !== 'default' && 
                 this.parameter.audience_values && 
                 this.parameter.audience_values[this.newValue.audience]) {
        const audienceName = this.audience(this.newValue.audience).name || 'this audience';
        if (!confirm(`Value for ${audienceName} already exists. Do you want to replace it?`)) {
          return;
        }
      }
      
      // Update the value
      this.setNewValue(data.value);
      
      // Update category and priority if provided
      if (data.category) {
        this.newValue.category = data.category;
      }
      if (data.priority !== undefined && data.priority !== '') {
        this.newValue.priority = Number(data.priority);
      }
      
      // Call addNewValue to save to database
      this.addNewValue();
      
      // Update original value and clear pending changes flag after successful submission
      if (this.newValue.audience) {
        this.originalValues[this.newValue.audience] = this.normalizeValue(data.value);
        this.$set(this.pendingChanges, this.newValue.audience, false);
      }
    },
    
    handleDefaultJsonSubmittedToDb(data) {
      // Update the value first
      this.changeAudienceValue('default', data.value);
      
      // Activate edit mode if not already
      if (!this.isEditing('default')) {
        this.activateEdit('default');
      }
      
      // Wait for DOM update then save
      this.$nextTick(() => {
        const textarea = document.getElementById('edit-value-default');
        if (!textarea) {
          alert('Error: Could not save to database. Please try again.');
          return;
        }
        textarea.value = data.value;
        this.editAudience('default');
        
        // Update original value and clear pending changes flag after successful submission
        this.originalValues['default'] = this.normalizeValue(data.value);
        this.$set(this.pendingChanges, 'default', false);
      });
    },
    
    handleAudienceJsonSubmittedToDb(data) {
      const audienceId = this.currentAudienceKey;
      
      // Update the value first
      this.changeAudienceValue(audienceId, data.value);
      
      // Update category and priority in parameter before saving
      const paramData = {
        ...this.parameter,
        audience_values: { ...this.parameter.audience_values }
      };
      
      if (data.category) {
        if (!paramData.categories) paramData.categories = {};
        paramData.categories[audienceId] = data.category;
      }
      if (data.priority !== undefined && data.priority !== '') {
        if (!paramData.priorities) paramData.priorities = {};
        paramData.priorities[audienceId] = Number(data.priority);
      }
      
      // Update local parameter state
      this.$set(this.parameter, 'categories', paramData.categories);
      this.$set(this.parameter, 'priorities', paramData.priorities);
      
      // Activate edit mode if not already
      if (!this.isEditing(audienceId)) {
        this.activateEdit(audienceId);
      }
      
      // Wait for DOM update then save
      this.$nextTick(() => {
        const textarea = document.getElementById(`edit-value-${audienceId}`);
        if (!textarea) {
          alert('Error: Could not save to database. Please try again.');
          return;
        }
        textarea.value = data.value;
        this.editAudience(audienceId);
        
        // Update original value and clear pending changes flag after successful submission
        this.originalValues[audienceId] = this.normalizeValue(data.value);
        this.$set(this.pendingChanges, audienceId, false);
      });
    },
    
    handleReadOnlyJsonSubmittedToDb(data) {
      const audienceId = this.readOnlyAudienceId;
      
      // Update the value first
      this.changeAudienceValue(audienceId, data.value);
      
      // Update category and priority in parameter before saving (if not default)
      if (audienceId !== 'default') {
        const paramData = {
          ...this.parameter,
          audience_values: { ...this.parameter.audience_values }
        };
        
        if (data.category) {
          if (!paramData.categories) paramData.categories = {};
          paramData.categories[audienceId] = data.category;
        }
        if (data.priority !== undefined && data.priority !== '') {
          if (!paramData.priorities) paramData.priorities = {};
          paramData.priorities[audienceId] = Number(data.priority);
        }
        
        // Update local parameter state
        this.$set(this.parameter, 'categories', paramData.categories);
        this.$set(this.parameter, 'priorities', paramData.priorities);
      }
      
      // Activate edit mode if not already
      if (!this.isEditing(audienceId)) {
        this.activateEdit(audienceId);
      }
      
      // Wait for DOM update then save
      this.$nextTick(() => {
        const textarea = document.getElementById(`edit-value-${audienceId}`);
        if (!textarea) {
          alert('Error: Could not save to database. Please try again.');
          return;
        }
        textarea.value = data.value;
        this.editAudience(audienceId);
        
        // Update original value and clear pending changes flag after successful submission
        this.originalValues[audienceId] = this.normalizeValue(data.value);
        this.$set(this.pendingChanges, audienceId, false);
      });
    },
    
  addNewValue() {
      if (this.newValue.audience) {
        const data = {
          ...this.parameter,
          audience_values: { ...this.parameter.audience_values },
        };
        const id = data.id;
        delete data.id;
        const valueToSave = this.newValueDoNotSend ? null : (this.newValue.value || '').trim();
        if (this.newValue.audience == "default") {
          data.default_value = valueToSave;
        } else {
          data.audience_values[this.newValue.audience] = valueToSave;
        }
        if (this.newValue.priority && Number(this.newValue.priority)) {
          if (!data.priorities) data.priorities = {};
          data.priorities[this.newValue.audience] = this.newValue.priority;
        }
        if(this.newValue.category) {
          data.categories[this.newValue.audience] = this.newValue.category;
        }
        this.$store.dispatch("configEdit", {
          type: "parameter",
          payload: { id, data, audienceNames: this.getAudienceNamesMap() },
        });
        this.newValue = {};
        this.newValueDoNotSend = false;
      } else {
        return;
      }
    },
    deleteConditionalValue(audienceId) {
      if (
        confirm(
          `You are trying to delete "${
            this.audience(audienceId).name
          }" audience. Are you sure?`
        )
      ) {
        const data = {
          ...this.parameter,
          audience_values: { ...this.parameter.audience_values },
        };
        const id = data.id;
        delete data.id;
        if (audienceId == "default") {
          delete data.default_value;
        } else {
          delete data.audience_values[audienceId];
        }
        this.$store.dispatch("configEdit", {
          type: "parameter",
          payload: { id, data, audienceNames: this.getAudienceNamesMap() },
        });
        this.newValue = {};
      }
    },
    activateEdit(audience) {
      const experiments = this.$store.getters.experiments;
      if (experiments) {
        const includedExperiments = experiments.filter((experiment) => {
          return (
            (experiment.status == "started" ||
              experiment.status == "created") &&
            experiment.variants[0][this.parameter.key]
          );
        });
        if (includedExperiments.length > 0) {
          let experimentNames = "";
          includedExperiments.forEach((experiment) => {
            experimentNames += experiment.name + ", ";
          });
          experimentNames = experimentNames.slice(0, -2);
          if (
            confirm(
              `You will edit a parameter that is actively used in ${experimentNames} experiments, it may break the experiment. Are you sure?`
            )
          ) {
            this.editingValues.push(audience);
          } else return;
        } else {
          this.editingValues.push(audience);
        }
      } else {
        this.editingValues.push(audience);
      }
    },
    //Changes audience value in input box before sending the changes to backend. Edits only applied when save button is clicked
    changeAudienceValue(key, val) {
  if (key === "default") {
    this.$set(this, 'default_value', val); // Ensure reactivity
  } else {
    this.parameter.audience_values[key] = val; // This should be reactive
  }
}
,
    isEditing(audience) {
      return this.editingValues.indexOf(audience) >= 0;
    },
    editAudience(audience) {
      const rawValue = document.getElementById(`edit-value-${audience}`).value;
      let finalValue;

      try {
        // First, try to process the input as a valid JSON.
        const parsedJSON = JSON.parse(rawValue);

        // If successful, re-stringify it to a compact format for storage.
        // This is the safest way to "clean" the JSON and ensure consistency.
        // It correctly preserves intentional newlines (\n) within strings.
        finalValue = JSON.stringify(parsedJSON);
      } catch (error) {
        // If JSON.parse fails, it's not a valid JSON object.
        // Treat it as a plain string value. We just trim whitespace.
        finalValue = rawValue.trim();

        // If the user seems to have attempted to write JSON but failed,
        // stop the save, show an error, and let them correct it.
        if (finalValue.startsWith("{") || finalValue.startsWith("[")) {
          document.getElementById("warning-modal-text").textContent =
            "The value is not a valid JSON. Please check your syntax and try again.";
          $("#warning-modal").modal("show");
          notify("Error: Invalid JSON not saved", "error", "top: 100px;");
          return;
        }
      }

      if (finalValue !== undefined) {
        const data = {
          ...this.parameter,
          audience_values: { ...this.parameter.audience_values },
        };
        const id = data.id;
        delete data.id;
        if (audience == "default") {
          data.default_value = finalValue;
        } else {
          data.audience_values[audience] = finalValue;
        }
        this.$store.dispatch("configEdit", {
          type: "parameter",
          payload: { id, data, audienceNames: this.getAudienceNamesMap() },
        });
        this.editingValues = this.editingValues.filter(
          (item) => item != audience
        );
        
        // Update original value and clear pending changes flag after successful database save
        this.originalValues[audience] = this.normalizeValue(finalValue);
        this.$set(this.pendingChanges, audience, false);
      } else {
        return;
      }
    },
    changePriority(audience) {
      try {
        const value = Number(
          document.getElementById(audience + "-priority").value
        );
        if (Number.isInteger(value)) {
          const data = JSON.parse(JSON.stringify(this.parameter));
          const id = data.id;
          delete data.id;
          if (!data.priorities) data.priorities = {};
          data.priorities[audience] = value;
          this.$store.dispatch("configEdit", {
            type: "parameter",
            payload: { id, data, audienceNames: this.getAudienceNamesMap() },
          });
          
          // Priority change doesn't affect pending changes for value, no need to clear
        }
      } catch (error) {
        console.log("error in changePriority method: ", error);
      }
    },
    isDisabled(audience) {
      return audience == "default" ? "disabled" : "";
    },
    getFocusedItem() {
      this.focusedItem = document.activeElement;
    },
    
    // Debug methods to track performance issues
    handleInputChange(event) {
      console.log('Input change detected, length:', event.target.value.length);
      // No JSON processing here - just logging
    },
    
    // Debounced input to prevent Vue reactivity issues with large strings
    debouncedInput(event) {
      const value = event.target.value;
      console.log('Debounced input, length:', value.length);
      
      // Clear previous timeout
      if (this._inputTimeout) {
        clearTimeout(this._inputTimeout);
      }
      
      // For very large strings, use a longer debounce
      const debounceTime = value.length > 50000 ? 1000 : 300;
      
      this._inputTimeout = setTimeout(() => {
        console.log('Updating newValue.value with length:', value.length);
        this.$set(this.newValue, 'value', value);
      }, debounceTime);
    },
    
    handlePaste(event) {
      console.log('Paste detected');
      console.time('Paste processing time');
      
      // Get the pasted data
      const pastedData = (event.clipboardData || window.clipboardData).getData('text');
      console.log('Pasted data length:', pastedData.length);
      
      // For very large pastes, handle specially
      if (pastedData.length > 50000) {
        console.log('Large paste detected, using optimized handling');
        event.preventDefault(); // Prevent default paste
        
        // Update the value directly without triggering Vue reactivity immediately
        event.target.value = pastedData;
        
        // Update Vue data with a delay
        setTimeout(() => {
          this.$set(this.newValue, 'value', pastedData);
          console.timeEnd('Paste processing time');
        }, 100);
      } else {
        // Let normal paste handling occur
        setTimeout(() => {
          console.timeEnd('Paste processing time');
          console.log('New value length:', this.newValue.value ? this.newValue.value.length : 0);
        }, 0);
      }
    },
    validateJSONInput(isEdit,audience) {
      try {
        if(audience)
        console.log(JSON.parse(document.getElementById(`edit-value-${audience}`).value))
        else    
      console.log(
          JSON.parse(document.getElementById(this.focusedItem.id).value)
  
        );
        notify("Valid JSON", "success");
      } catch {
        if (!isEdit) {
          document.getElementById("warning-modal-text").textContent =
            "Focused area is NOT a valid JSON";
        }
        $("#warning-modal").modal("show");
        notify("Invalid JSON", "error", "top: 100px;");
      }
    },
  },
};
</script>
<style lang="scss" scoped>
  @import "../components/common/styles/form_elements.scss";
</style>

<style scoped>
.flex1 {
  color: rgba(255, 255, 255, 0.8);
}

.pre {
  white-space: pre-wrap;
}

.action-button {
  margin: auto;
  padding: 1px 5px;
  float: right;
}

.value {
  text-overflow: ellipsis;
  overflow: hidden;
  height: auto;
  max-height: 60px;
  word-break: break-all;
}

.new-value-input
{
  display: flex;

}

.params-hover {
  margin: 1em 0em !important;
}

.params-hover:hover {
  padding: 10px 0px;
  margin: -10px 0px;
}

.arrow {
  position: absolute;
  bottom: 1em;
  right: 2em;
}

.aud-val {
  padding: 0px 9px;
}

.parameter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.parameter-header h1 {
  margin: 0;
}

.history-button {
  padding: 0.5rem 1rem;
}

.send-toggle-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.4rem;
}

.send-toggle-text {
  font-size: 0.8rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  flex-shrink: 0;
  margin: 0;
  cursor: pointer;
}

.toggle-switch.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  inset: 0;
  background-color: #555;
  border-radius: 20px;
  transition: background-color 0.2s ease;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  height: 14px;
  width: 14px;
  left: 3px;
  bottom: 3px;
  background-color: #fff;
  border-radius: 50%;
  transition: transform 0.2s ease;
}

.toggle-switch input:checked + .toggle-slider {
  background-color: #28a745;
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(16px);
}

.toggle-spinner {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
}

.not-sent-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.25rem 0.5rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.8rem;
  font-style: italic;
}

.not-sent-badge i {
  font-size: 0.7rem;
}

</style>
